import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  InputNumber,
  Upload,
  message,
  Tag,
  Divider,
  Space,
  Row,
  Col,
} from "antd";
import {
  CarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  UserOutlined,
  UploadOutlined,
  DollarOutlined,
  CopyOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import NumberFormat from "react-number-format";
import { DateTime } from "luxon";
import useDeliverySocket from "../../../hooks/useDeliverySocket";
import { getDistanceWithFallback } from "../../../utils/distance";
const { TextArea } = Input;

const DeliveryDetailsModal = ({
  visible,
  order,
  onCancel,
  onPickupOrder,
  onDeliverOrder,
  onReportBuyerAbsent,
  loading,
  onStartDelivery,
  onArrived,
}) => {
  const [deliverForm] = Form.useForm();
  const [absentForm] = Form.useForm();
  const [buyerCode, setBuyerCode] = useState("");
  const [snapshotOfTracker, setSnapshotOfTracker] = useState(null);
  const [snapshotOfCall, setSnapshotOfCall] = useState(null);
  const [copiedPickupCode, setCopiedPickupCode] = useState(false);
  const [liveDistance, setLiveDistance] = useState(null);
  const [liveEta, setLiveEta] = useState(null);

  const handleSocketUpdate = useCallback((data) => {
    if (data.distance !== undefined) {
      setLiveDistance(data.distance);
    }
    if (data.eta !== undefined) {
      setLiveEta(data.eta);
    }
  }, []);

  const { joinDeliveryRoom, leaveDeliveryRoom } = useDeliverySocket(
    null,
    null,
    handleSocketUpdate,
    null
  );

  useEffect(() => {
    if (visible && order?.delivery?.id) {
      setLiveDistance(null);
      setLiveEta(null);
      joinDeliveryRoom(order.delivery.id);
      return () => leaveDeliveryRoom(order.delivery.id);
    }
  }, [visible, order?.delivery?.id, joinDeliveryRoom, leaveDeliveryRoom]);

  const getStatusColor = (status) => {
    const upperStatus = status?.toUpperCase();
    switch (upperStatus) {
      case "READY":
        return "orange";
      case "PICKED":
        return "blue";
      case "IN_TRANSIT":
        return "cyan";
      case "ARRIVED":
        return "purple";
      case "DELIVERED":
        return "green";
      case "RETURNED":
        return "red";
      default:
        return "default";
    }
  };

  const copyPickupCode = () => {
    const code = order?.delivery?.pickupCode;
    if (code) {
      navigator.clipboard.writeText(code);
      setCopiedPickupCode(true);
      message.success("Pickup code copied!");
      setTimeout(() => setCopiedPickupCode(false), 2000);
    }
  };

  const handleStartDelivery = async () => {
    const result = await onStartDelivery(order.delivery.id);
    if (result.success) {
      onCancel();
    }
  };

  const handleArrived = async () => {
    const result = await onArrived(order.delivery.id);
    if (result.success) {
      onCancel();
    }
  };

  const handleDeliver = async () => {
    if (!buyerCode) {
      message.error("Please enter the buyer code");
      return;
    }

    await onDeliverOrder(order.delivery.id, buyerCode);

    deliverForm.resetFields();
    setBuyerCode("");
    onCancel();
  };

  const handleBuyerAbsent = async () => {
    if (!snapshotOfTracker || !snapshotOfCall) {
      message.error("Please upload both snapshots");
      return;
    }

    const formData = new FormData();
    formData.append("files", snapshotOfTracker.file);
    formData.append("files", snapshotOfCall.file);

    const result = await onReportBuyerAbsent(order.delivery.id, formData);

    if (result.success) {
      absentForm.resetFields();
      setSnapshotOfTracker(null);
      setSnapshotOfCall(null);
      onCancel();
    }
  };

  const deliveryStatus = order?.delivery?.deliveryStatus?.toUpperCase();

  return (
    <Modal
      title="Delivery Details"
      open={visible}
      onCancel={onCancel}
      footer={
        <div style={{ textAlign: "right" }}>
          <Button onClick={onCancel} size="large">
            Close
          </Button>
        </div>
      }
      width={700}
      destroyOnClose
      closable={true}
      maskClosable={false}
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "8px" }}
      style={{ maxHeight: "90vh" }}
    >
      {order && (
        <div className="space-y-4">
          {/* Order Header */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div>
                  <div className="text-xs text-gray-600 font-semibold">
                    Order Code
                  </div>
                  <div className="text-lg font-mono font-bold">
                    {order?.order?.orderCode}
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div>
                  <div className="text-xs text-gray-600 font-semibold">
                    Status
                  </div>
                  <Tag
                    color={getStatusColor(deliveryStatus)}
                    style={{ fontSize: "12px", padding: "4px 8px" }}
                  >
                    {deliveryStatus}
                  </Tag>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div>
                  <div className="text-xs text-gray-600 font-semibold">
                    Order Amount
                  </div>
                  <div className="text-lg font-bold">
                    <NumberFormat
                      thousandSeparator={true}
                      prefix="₦"
                      displayType="text"
                      value={parseFloat(order?.order?.totalAmount || 0).toFixed(
                        2,
                      )}
                    />
                  </div>
                </div>
              </Col>
              {!order.delivery.restaurantAcceptedDelivery && (
                <Col xs={24} sm={12}>
                  <div>
                    <div className="text-xs text-gray-600 font-semibold">
                      Delivery Fee
                    </div>
                    <div className="text-lg font-bold">
                      <NumberFormat
                        thousandSeparator={true}
                        prefix="₦"
                        displayType="text"
                        value={parseFloat(
                          order?.deliveryFeeEarnings?.deliveryFeeKonnect || 0,
                        ).toFixed(2)}
                      />
                    </div>
                  </div>
                </Col>
              )}
              {order.delivery.distance && order.delivery.trackerLink && (
                <Col xs={24}>
                  <div className="bg-white/50 border border-blue-200 rounded-xl p-4 shadow-sm mb-2">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <EnvironmentOutlined className="text-blue-600" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 leading-none mb-1">Distance</p>
                          <p className="text-base font-bold text-gray-900 leading-none">
                            {liveDistance !== null ? liveDistance : getDistanceWithFallback(order?.delivery?.trackerLink, order?.delivery?.distance)} km
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="bg-green-100 p-2 rounded-lg">
                          <ClockCircleOutlined className="text-green-600" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 leading-none mb-1">Arrival</p>
                          <p className="text-base font-bold text-gray-900 leading-none">
                            {liveEta ? DateTime.fromISO(liveEta).toFormat("h:mm a") : (order.delivery.eta ? DateTime.fromISO(order.delivery.eta).toFormat("h:mm a") : "...")}
                          </p>
                        </div>
                      </div>
                    </div>

                    <Button
                      type="primary"
                      size="large"
                      className="w-full bg-green-600 hover:bg-green-700 border-none h-12 rounded-xl flex items-center justify-center gap-2 font-bold text-base shadow-md transition-all active:scale-[0.98]"
                      onClick={() => window.open(order.delivery.trackerLink, '_blank')}
                      icon={<EnvironmentOutlined style={{ fontSize: '18px' }} />}
                    >
                      VIEW LIVE TRACKING MAP
                    </Button>
                  </div>
                </Col>
              )}
            </Row>
          </div>

          {/* Customer & Restaurant Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-3">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <UserOutlined /> Delivery Details
              </h4>
              {deliveryStatus !== "DELIVERED" && (
                <>
                  <p>
                    <strong>Buyer Name:</strong> {order.customer?.name || "N/A"}
                  </p>
                  <p>
                    <strong>Buyer Phone:</strong> {order.customer?.phoneCode}
                    {order.customer?.phone || "N/A"}
                  </p>
                </>
              )}
              <p className="mt-3 mb-1">
                <strong>Delivery Address:</strong>
              </p>
              <div className="bg-gray-50 p-2 rounded">
                <div className="text-gray-700">
                  {order.savedAddress?.address || "N/A"}
                </div>
                {order.savedAddress?.address && (
                  <Button
                    type="link"
                    icon={<EnvironmentOutlined />}
                    onClick={() => {
                      const address = encodeURIComponent(order.savedAddress.address);
                      window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, "_blank");
                    }}
                    style={{ paddingLeft: 0, marginTop: "8px" }}
                  >
                    Open in Google Maps
                  </Button>
                )}
              </div>
            </div>

            <div className="border rounded-lg p-3">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <EnvironmentOutlined /> Restaurant
              </h4>
              {deliveryStatus !== "DELIVERED" && (
                <>
                  <p>
                    <strong>Name:</strong> {order.business?.name || "N/A"}
                  </p>
                  <p>
                    <strong>Phone:</strong> {order.business?.phone || "N/A"}
                  </p>
                </>
              )}
              <p>
                <strong>Address:</strong> {order.order?.pickupAddress || order.pickupAddress || order.business?.pickupAddressString || order.business?.address || "N/A"}
              </p>
            </div>
          </div>

          {/* Pickup Code Section */}
          {deliveryStatus === "READY" && (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-900 mb-3">
                Pickup Code
              </h4>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-white border border-yellow-300 rounded px-3 py-2 font-mono text-lg font-bold">
                  {order?.delivery?.pickupCode || "N/A"}
                </div>
                <Button
                  icon={copiedPickupCode ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={copyPickupCode}
                  type="primary"
                  style={{
                    backgroundColor: copiedPickupCode ? "#52c41a" : undefined,
                  }}
                >
                  {copiedPickupCode ? "Copied" : "Copy"}
                </Button>
              </div>
              <p className="text-xs text-yellow-700 mt-2">
                Share this code with the restaurant staff
              </p>
            </div>
          )}


          {/* Order Items */}
          <div>
            <h4 className="font-semibold mb-3 text-gray-900">Order Items</h4>
            <div className="space-y-2">
              {order?.items?.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between p-3 bg-gray-50 rounded border border-gray-200"
                >
                  <div className="flex-1">
                    <div className="font-medium ">{item.menuItem?.name}</div>
                    <div className=" text-gray-600">Qty: {item.quantity}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold ">
                      <NumberFormat
                        thousandSeparator={true}
                        prefix="₦"
                        displayType="text"
                        value={parseFloat(item.price).toFixed(2)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Divider>Actions</Divider>

          {deliveryStatus === "READY" && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className=" text-blue-700">
                Waiting for restaurant to verify the pickup code. You'll be able
                to start delivery once verified.
              </p>
            </div>
          )}

          {deliveryStatus === "PICKED" && (
            <Button
              type="primary"
              icon={<CarOutlined />}
              onClick={handleStartDelivery}
              loading={loading}
              block
              size="large"
            >
              Start Delivery
            </Button>
          )}

          {deliveryStatus === "IN_TRANSIT" && (
            <Button
              type="primary"
              icon={<EnvironmentOutlined />}
              onClick={handleArrived}
              loading={loading}
              block
              size="large"
            >
              Mark as Arrived
            </Button>
          )}

          {deliveryStatus === "ARRIVED" && (
            <Space direction="vertical" className="w-full">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className=" text-yellow-700 font-semibold">
                  Enter the 6-digit code given by the buyer to complete delivery
                </p>
              </div>
              <Form form={deliverForm} layout="vertical">
                <Form.Item label="Buyer Code" required>
                  <Input
                    placeholder="Enter 6-digit code"
                    value={buyerCode}
                    onChange={(e) => setBuyerCode(e.target.value)}
                    maxLength={6}
                    size="large"
                  />
                </Form.Item>

                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={handleDeliver}
                  loading={loading}
                  block
                  size="large"
                  className="mb-2"
                >
                  Complete Delivery
                </Button>
              </Form>

              <Button
                danger
                onClick={() => {
                  setTimeout(() => {
                    const absentSection =
                      document.getElementById("buyer-absent-form");
                    if (absentSection)
                      absentSection.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                }}
                block
                size="large"
              >
                Buyer Not Available
              </Button>
            </Space>
          )}

          {(deliveryStatus === "DELIVERED" ||
            deliveryStatus === "RETURNED") && (
              <div
                className={`text-center py-4 rounded-lg ${deliveryStatus === "DELIVERED"
                  ? "bg-green-50 border border-green-200"
                  : "bg-red-50 border border-red-200"
                  }`}
              >
                <CheckCircleOutlined
                  className={`text-3xl mb-2 ${deliveryStatus === "DELIVERED"
                    ? "text-green-600"
                    : "text-red-600"
                    }`}
                />
                <p
                  className={`font-semibold ${deliveryStatus === "DELIVERED"
                    ? "text-green-800"
                    : "text-red-800"
                    }`}
                >
                  Order{" "}
                  {deliveryStatus === "DELIVERED" ? "Delivered" : "Returned"}
                </p>
              </div>
            )}

          {/* Buyer Absent Form */}
          <div id="buyer-absent-form">
            {deliveryStatus === "ARRIVED" && (
              <>
                <Divider>Buyer Absent Report</Divider>

                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 mb-4">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-3">
                    ⚠️ Buyer Absent Report
                  </h3>
                  <p className="text-yellow-700 mb-3">
                    <strong>Please hang around for a few minutes.</strong>
                  </p>
                  <p className="text-yellow-700 mb-3">
                    Text message has been sent to the buyer to contact you.
                    While you wait, kindly do the following:
                  </p>
                  <ol className="list-decimal list-inside space-y-1 text-yellow-800 ">
                    <li>Stay in the delivery location</li>
                    <li>Keep your phone available</li>
                    <li>Take screenshots of your location tracker</li>
                    <li>Try calling the buyer if possible</li>
                  </ol>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default DeliveryDetailsModal;
